import { PLAYER_TYPES } from '@/Components/VideoPlayer/Constant';
import { getUnifiedSessionId } from '@/Providers/BrowserSessionProvider';
import Sentry from './Sentry';

let nonceLoader: any | null = null;
let initPALPromise: Promise<void> | null = null;

declare global {
  interface Window {
    goog?: any;
  }
}

function waitForPALSdk(): Promise<void> {
  return new Promise((resolve, reject) => {
    const sdkScript = document.getElementById('pal-sdk') as HTMLScriptElement;
    if (!sdkScript) {
      reject(new Error('PAL SDK script tag not found'));
      return;
    }

    if (window.goog?.pal) {
      // Already loaded
      resolve();
    } else {
      sdkScript.addEventListener('load', () => {
        resolve();
      });

      sdkScript.addEventListener('error', () => {
        reject(new Error('Failed to load PAL SDK'));
      });
    }
  });
}

/**
 * Initializes the PAL loader.
 *
 * @param allowStorage Whether PAL is allowed to use local storage (based on user consent).
 */
export function initPAL(allowStorage = false): Promise<void> {
  if (!initPALPromise) {
    initPALPromise = (async () => {
      try {
        waitForPALSdk().then(() => {
          const pal = window.goog?.pal;
          if (!pal || !pal.NonceLoader || !pal.NonceRequest || !pal.ConsentSettings) {
            Sentry.captureMessage('PAL SDK is not loaded or incomplete.');
            return;
          }
          const consentSettings = new pal.ConsentSettings();
          consentSettings.allowStorage = allowStorage;
          nonceLoader = new pal.NonceLoader(consentSettings);
        });
      } catch (error) {
        Sentry.captureMessage(error);
      }
    })();
  }
  return initPALPromise;
}
/**
 * Generates a GIVN nonce using PAL.
 *
 * @param isDASH Whether the video is using DASH (vs Brightcove).
 * @param ppid A hashed ID uniquely identifying the user or profile.
 * @param sessionId A session ID.
 * @returns The generated GIVN nonce string, or null if failed.
 */
export async function generateNonce(
  isDASH: boolean,
  ppid: string,
  sessionId: string,
): Promise<string | null> {
  const pal = window.goog?.pal;
  await initPAL();
  if (!pal || !pal.NonceRequest || !nonceLoader) {
    Sentry.captureMessage('PAL SDK is not loaded or nonceLoader not initialized.');
    return null;
  }
  const request = new pal.NonceRequest();
  request.adWillAutoPlay = true;
  request.adWillPlayMuted = false;
  request.videoPlayerWidth = 640;
  request.videoPlayerHeight = 480;
  request.playerType = isDASH ? PLAYER_TYPES.SHAKA : PLAYER_TYPES.BRIGHTCOVE;
  request.playerVersion = isDASH ? '3.0.1' : '7.35.0';
  request.omidPartnerName = 'amazon2';
  request.omidPartnerVersion = '1.0.0';
  request.ppid = ppid;
  request.sessionId = getUnifiedSessionId() ?? sessionId;

  try {
    const nonceManager = await nonceLoader.loadNonceManager(request);
    const givn = nonceManager.getNonce();

    if (!givn) {
      Sentry.captureMessage('GIVN not returned from nonceManager.');
      return null;
    }

    return givn;
  } catch (err) {
    Sentry.captureException(err);
    return null;
  }
}
